import React, { useEffect, useState } from 'react';
import { useDolphin } from '../../hooks/useDolphin';
import { useWallet } from '../../context/WalletContext';
import ConnectPrompt from '../../components/common/ConnectPrompt';
import { FaLink, FaGem, FaUsers, FaDollarSign } from 'react-icons/fa';
import Tree from 'react-d3-tree';
import ENSAvatar from '../../components/common/ENSAvatar';
import Badge from '../../components/common/Badge';
import Tooltip from '../../components/common/Tooltip';
import UserModal from '../../components/common/UserModal';

const statCards = [
  {
    key: 'inviteCode',
    label: 'Invite Code',
    icon: <FaLink className="stat-icon" />, 
    color: 'var(--primary)',
    tooltip: 'Your unique invite code (click to copy)',
  },
  {
    key: 'ownedLevels',
    label: 'Levels Owned',
    icon: <FaGem className="stat-icon" />, 
    color: '#a78bfa',
    tooltip: 'Levels you have unlocked',
  },
  {
    key: 'totalDownlines',
    label: 'Total Downlines',
    icon: <FaUsers className="stat-icon" />, 
    color: '#38bdf8',
    tooltip: 'Number of direct downlines',
  },
  {
    key: 'spent',
    label: 'Total Spent',
    icon: <FaDollarSign className="stat-icon" />, 
    color: '#fbbf24',
    tooltip: 'Total USDT spent on platform',
    suffix: 'USD',
  },
];

const UserAnalytics = () => {
  const { isConnected } = useWallet();
  const { referralStats, userStats, fetchReferralStats, downlineTree, fetchFullDownlineTree, formatInviteCode, getTotalSpentUSDT } = useDolphin();
  const [viewingSubtree, setViewingSubtree] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Tree selection state for dropdown
  const [selectedTreeIdx, setSelectedTreeIdx] = useState(0);
  // State for selected downline tree and loading
  const [selectedDownlineTree, setSelectedDownlineTree] = useState(null);
  // Removed unused variable: selectedAddress
  const [treeLoading, setTreeLoading] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchReferralStats();
    } catch (err) {
      setError('Failed to refresh referral stats');
    }
    setRefreshing(false);
  };

  useEffect(() => {
    if (!referralStats || !userStats) return;
    // Debugging output
    console.log('[UserAnalytics] referralStats:', referralStats);
    console.log('[UserAnalytics] userStats:', userStats);
    // Fetch total spent from contract
    (async () => {
      let spent = '0';
      if (userStats && userStats.address) {
        spent = await getTotalSpentUSDT(userStats.address);
      } else if (window.ethereum && window.ethereum.selectedAddress) {
        spent = await getTotalSpentUSDT(window.ethereum.selectedAddress);
      }
      setAnalytics({
        inviteCode: referralStats.inviteCode || '',
        ownedLevels: (userStats && userStats.levels) ? userStats.levels : (referralStats.levels || []),
        totalDownlines: referralStats.downlineCount ?? 0,
        referralEarnings: referralStats.totalEarned ?? '0',
        spent: spent,
        vesting: referralStats.vesting ?? null,
        isSpecial: referralStats.isSpecial ?? false,
        downlinesByRank: referralStats.downlinesByRank || [[], [], [], [], []],
        referrer: referralStats.referrer || null,
        referralPayouts: referralStats.referralPayouts ?? null,
        fixedPayouts: referralStats.fixedPayouts ?? null,
        miningPayouts: referralStats.miningPayouts ?? null,
      });
      setLoading(false);
      setError(null);
    })();
  }, [referralStats, userStats, getTotalSpentUSDT]);

  const handleCopy = () => {
    if (!analytics?.inviteCode) return;
    navigator.clipboard.writeText(analytics.inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const renderStatValue = (card, analytics) => {
    switch (card.key) {
      case 'inviteCode': {
        const formatted = analytics.inviteCode ? formatInviteCode(analytics.inviteCode) : '--';
        return (
          <span
            className="invite-code-ellipsis"
            style={{
              display: 'inline-block',
              maxWidth: '140px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              verticalAlign: 'bottom',
              cursor: 'pointer',
              color: '#f472b6',
            }}
            onClick={handleCopy}
            title={analytics.inviteCode}
          >
            {formatted}
            {analytics.inviteCode && (
              <span style={{ marginLeft: 8, fontSize: '0.6em', color: copied ? '#22d3ee' : '#a1a1aa' }}>
                {copied ? 'Copied!' : '📋'}
              </span>
            )}
          </span>
        );
      }
      case 'ownedLevels': {
        const levels = analytics.ownedLevels || [];
        if (!levels.length) return '--';
        const highest = Math.max(...levels);
        return `Level ${highest} unlocked`;
      }
      case 'isSpecial':
        return analytics.isSpecial ? 'Yes' : 'No';
      default:
        return analytics[card.key] || 0;
    }
  };

  if (!isConnected && localStorage.getItem('referralPromptSkipped') !== 'true') return <ConnectPrompt />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!analytics) return <div className="text-gray-400 p-4">Loading analytics...</div>;
  // Show stat cards immediately, even if loading

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white overflow-x-hidden w-full">
      <style>{`
        /* Prevent iOS zoom on input/select */
        input, select, textarea, button {
          font-size: 16px !important;
        }
        .ua-card-label { font-size: 0.92rem !important; }
        .ua-card-value { font-size: 1rem !important; }
        .ua-header-title { font-size: 1.08rem !important; }
        .ua-header-desc { font-size: 0.85rem !important; }
        .ua-tree-label { font-size: 0.95rem !important; }
        @media (max-width: 640px) {
          .ua-card-label { font-size: 0.78rem !important; }
          .ua-card-value { font-size: 0.88rem !important; }
          .ua-header-title { font-size: 0.92rem !important; }
          .ua-header-desc { font-size: 0.7rem !important; }
          .ua-tree-label { font-size: 0.78rem !important; }
          .ua-mobile-px { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
          .ua-mobile-py { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
          .ua-mobile-rounded { border-radius: 0.6rem !important; }
          .ua-mobile-gap { gap: 0.5rem !important; }
        }
      `}</style>
      <div className="w-full flex flex-col gap-3 sm:gap-4 md:gap-6 lg:gap-8 ua-mobile-gap">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 ua-mobile-gap">
          <div className="p-1.5 sm:p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl ua-mobile-rounded shadow-lg flex items-center justify-center">
            <FaGem className="w-4 h-4 sm:w-6 sm:h-6 text-white drop-shadow-lg" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-extrabold tracking-tight text-white truncate ua-header-title">User Analytics</h1>
            <p className="text-[11px] sm:text-sm text-gray-400 truncate ua-header-desc">Your stats & referral tree</p>
          </div>
        </div>

        {/* Referrer Status & Refresh */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full items-stretch ua-mobile-gap">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl ua-mobile-rounded p-2 sm:p-4 border border-cyan-700 shadow-lg flex-1 flex items-center gap-2 ua-mobile-px ua-mobile-py">
            <span className="font-semibold text-cyan-300 shrink-0">Referrer:</span>
            {analytics && analytics.referrer && analytics.referrer !== '0x0000000000000000000000000000000000000000' ? (
              <span className="font-mono text-emerald-400 truncate max-w-[180px] sm:max-w-xs px-2 py-1 rounded bg-gray-800/60 border border-cyan-700" title={analytics.referrer} style={{overflowX:'auto', display:'inline-block'}}>{analytics.referrer}</span>
            ) : (
              <span className="text-red-400">No referrer set</span>
            )}
          </div>
          <div className="flex items-center flex-1">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-xl ua-mobile-rounded font-semibold text-xs sm:text-sm shadow-md transition-all ${refreshing ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white'}`}
            >
              {refreshing ? 'Refreshing...' : 'Refresh Referral Stats'}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 ua-mobile-gap w-full mb-4 sm:mb-5 text-left items-stretch">
          {statCards.filter(card => card.key !== 'referralEarnings').map(card => (
            <div
              className="bg-white/10 dark:bg-gray-900/60 rounded-md ua-mobile-rounded p-1 sm:p-2 border border-cyan-200/20 shadow group flex-1 flex flex-col items-start justify-between min-w-0 transition-all duration-200 hover:scale-[1.03] hover:shadow-cyan-400/10 hover:border-cyan-300/60 cursor-pointer min-h-[44px] sm:min-h-[64px]"
              key={card.key}
              title={card.tooltip}
              style={{ minWidth: '0' }}
            >
              <div className="flex items-center mb-0.5 sm:mb-2 w-full gap-1 sm:gap-2">
                <span
                  className="p-0.5 sm:p-1 rounded bg-cyan-100/30 dark:bg-cyan-900/40 flex items-center justify-center shadow group-hover:bg-cyan-200/40 transition-all"
                  style={{ minWidth: '18px', minHeight: '18px', fontSize: '0.85rem', background: `${card.color}20` }}
                >
                  {card.icon}
                </span>
                <span className="text-[11px] sm:text-xs text-cyan-700 dark:text-cyan-200 font-semibold truncate w-full group-hover:text-cyan-500 transition-all ua-card-label" style={{maxWidth:'70px'}}>{card.label}</span>
              </div>
              <div className="text-xs sm:text-sm font-bold text-emerald-600 dark:text-emerald-300 truncate w-full text-left group-hover:text-emerald-400 transition-all ua-card-value min-h-[18px] sm:min-h-[22px] flex items-center">
                {loading || !analytics ? (
                  <span className="inline-block w-12 h-3.5 sm:w-16 sm:h-4 bg-gray-700/40 rounded animate-pulse" style={{minWidth:36}}></span>
                ) : (
                  renderStatValue(card, analytics)
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Referral Tree Visualization */}
        <div className="mt-4 sm:mt-8 w-full ua-mobile-px">
          <h3 className="text-base sm:text-lg font-extrabold mb-2 sm:mb-3 text-cyan-300 tracking-tight ua-tree-label">Your Referral Trees</h3>
          {(() => {
            const children = downlineTree?.data?.children || [];
            // If only one direct downline, auto-load their tree
            if (children.length === 1) {
              // If not loaded, auto-load the tree
              if (!selectedDownlineTree && !treeLoading) {
                (async () => {
                  setTreeLoading(true);
                  setViewingSubtree(true);
                  const tree = await fetchFullDownlineTree.call(null, 3, 50, children[0].address);
                  setSelectedDownlineTree(tree);
                  setTreeLoading(false);
                })();
                return (
                  <div className="w-full flex flex-col items-center justify-center py-4 sm:py-6">
                    <div className="animate-spin rounded-full h-7 w-7 sm:h-8 sm:w-8 border-t-2 border-b-2 border-cyan-400 mb-2 sm:mb-3"></div>
                    <div className="text-cyan-300 text-center text-sm sm:text-base font-semibold">Loading your only direct referral's tree...</div>
                  </div>
                );
              }
              // If loaded, show the tree directly
              if (selectedDownlineTree && selectedDownlineTree.address) {
                return (
                  <div className="w-full flex flex-col items-center">
                    <div className="backdrop-blur-lg bg-gradient-to-br from-cyan-900/80 to-gray-900/80 rounded-xl ua-mobile-rounded border-2 border-cyan-400 shadow-2xl p-4 sm:p-4 md:p-6 relative w-full max-w-full" style={{ minHeight: '320px', height: '60vw', maxHeight: '70vh', overflow: 'auto' }}>
                      <div className="absolute top-2 right-2 flex gap-2 z-10">
                        <button
                          className="px-1.5 py-1 rounded-lg font-semibold text-[10px] sm:text-xs shadow bg-gradient-to-r from-pink-500 to-cyan-500 text-white hover:from-pink-600 hover:to-cyan-600 transition-all border border-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                          onClick={async () => {
                            setTreeLoading(true);
                            setViewingSubtree(false);
                            const tree = await fetchFullDownlineTree.call(null, 3, 50);
                            setSelectedDownlineTree(tree);
                            setTreeLoading(false);
                          }}
                        >
                          ← Back
                        </button>
                        <button
                          className="px-1.5 py-1 rounded-lg font-semibold text-[10px] sm:text-xs shadow bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600 transition-all border border-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                          onClick={async () => {
                            setTreeLoading(true);
                            const tree = await fetchFullDownlineTree.call(null, 3, 50, selectedDownlineTree.address);
                            setSelectedDownlineTree(tree);
                            setTreeLoading(false);
                          }}
                        >
                          ⟳ Refresh
                        </button>
                      </div>
                      <div className="w-full h-full min-h-[240px] sm:min-h-[320px]" style={{overflow:'auto'}}>
                        <Tree
                          data={mapDownlineTreeToD3(selectedDownlineTree)}
                          orientation="vertical"
                          translate={{ x: window.innerWidth < 640 ? 180 : 400, y: 120 }}
                          zoomable={true}
                          collapsible={true}
                          transitionDuration={600}
                          separation={{ siblings: 3.5, nonSiblings: 4.5 }}
                          renderCustomNodeElement={({ nodeDatum, toggleNode }) => (
                            <g style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setModalUser(nodeDatum); }}>
                              <defs>
                                <radialGradient id={`grad-${nodeDatum.address}`} cx="50%" cy="50%" r="50%">
                                  <stop offset="0%" stopColor="#fff" stopOpacity="0.7" />
                                  <stop offset="100%" stopColor={nodeDatum.isSpecial ? '#f472b6' : nodeDatum.isTopReferrer ? '#38bdf8' : '#22d3ee'} stopOpacity="0.9" />
                                </radialGradient>
                              </defs>
                              <circle r={nodeDatum.__rd3t.depth === 0 ? 30 : 22} fill={`url(#grad-${nodeDatum.address})`} stroke="#fff" strokeWidth="2.5" />
                              <foreignObject x="-40" y="-60" width="80" height="60">
                                <div style={{display:'flex',justifyContent:'center',alignItems:'center'}}>
                                  <ENSAvatar address={nodeDatum.address} size={54} />
                                </div>
                              </foreignObject>
                              <Tooltip content={<div style={{minWidth:200}}>
                                <div className="font-mono text-cyan-300 text-xs mb-1" style={{wordBreak:'break-all'}}>{nodeDatum.address}</div>
                                {nodeDatum.inviteCode && <div className="text-xs text-pink-400">Invite: {nodeDatum.inviteCode}</div>}
                                {nodeDatum.earnings && <div className="text-xs text-emerald-400">Earned: {nodeDatum.earnings} USDT</div>}
                                {nodeDatum.level && <div className="text-xs text-cyan-200">Level: {nodeDatum.level}</div>}
                                {nodeDatum.joinDate && <div className="text-xs text-yellow-300">Joined: {nodeDatum.joinDate}</div>}
                              </div>}>
                                <text fill="#fff" stroke="#000" strokeWidth="0.5" x="0" y="48" textAnchor="middle" fontSize="18" fontWeight="bold">
                                  {nodeDatum.name}
                                </text>
                              </Tooltip>
                              {nodeDatum.isSpecial && (
                                <Badge label="Special" color="#f472b6" className="absolute left-1/2 -translate-x-1/2 mt-1" />
                              )}
                              {nodeDatum.isTopReferrer && (
                                <Badge label="Top Referrer" color="#38bdf8" className="absolute left-1/2 -translate-x-1/2 mt-1" />
                              )}
                            </g>
                          )}
                          styles={{
                            nodes: {
                              node: {
                                circle: { filter: 'drop-shadow(0 1px 8px #22d3ee66)' },
                                name: { fontSize: '20px', fill: '#fff', fontWeight: 700, filter: 'drop-shadow(0 1px 2px #fff6)' },
                                attributes: { fontSize: '16px', fill: '#a7f3d0' },
                              },
                              leafNode: {
                                circle: { filter: 'drop-shadow(0 1px 6px #a7f3d066)' },
                                name: { fontSize: '18px', fill: '#fff' },
                                attributes: { fontSize: '14px', fill: '#a1a1aa' },
                              },
                            },
                            links: { stroke: 'url(#link-gradient)', strokeWidth: 3.5, opacity: 0.95 },
                          }}
                        />
                        <UserModal user={modalUser} onClose={() => setModalUser(null)} />
                      </div>
                    </div>
                  </div>
                );
              }
            }
            // If more than one direct downline, show selector
            if (children.length > 1 || (analytics?.downlinesByRank && analytics.downlinesByRank[0]?.length > 1)) {
              return (
                <div className="flex flex-col items-start w-full">
                  {/* New selector for Level 1 referrals */}
                  <label htmlFor="referralSelector" className="text-base text-cyan-200 mb-2 font-semibold tracking-wide ua-tree-label">Select Level 1 Referral:</label>
                  <div className="flex flex-wrap gap-1 mb-3 w-full ua-mobile-gap">
                    {(children.length > 0 ? children : analytics.downlinesByRank[0]).map((child, idx) => {
                      const address = child.address || child;
                      return (
                        <button
                          key={address}
                          id={"referralSelector"}
                          type="button"
                          className={`px-2 py-1.5 rounded-md font-semibold text-[11px] sm:text-xs shadow transition-all ${selectedTreeIdx === idx ? 'bg-cyan-600 text-white border border-cyan-400' : 'bg-gray-100 text-cyan-700 border border-cyan-200'} hover:bg-cyan-500 hover:text-white`}
                          style={{ minWidth: '72px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2, letterSpacing: '0.01em', boxShadow: '0 1px 4px #38bdf822' }}
                          onClick={async () => {
                            setSelectedTreeIdx(idx);
                            setTreeLoading(true);
                            setViewingSubtree(true);
                            const tree = await fetchFullDownlineTree.call(null, 3, 50, address);
                            setSelectedDownlineTree(tree);
                            setTreeLoading(false);
                          }}
                          aria-label={`Select referral ${address}`}
                        >
                          {address.length > 12 ? `${address.slice(0, 6)}...${address.slice(-4)}` : address}
                        </button>
                      );
                    })}
                  </div>
                  <div className="w-full">
                    <div className="backdrop-blur-lg bg-gradient-to-br from-cyan-900/80 to-gray-900/80 rounded-2xl ua-mobile-rounded border-2 border-cyan-400 shadow-2xl p-4 sm:p-6 relative" style={{ width: '100%', height: '480px', maxHeight: '70vh', overflow: 'auto' }}>
                      {treeLoading ? (
                        <div className="text-cyan-300 py-20 text-center text-lg sm:text-xl font-bold tracking-wide">Loading tree...</div>
                      ) : selectedDownlineTree ? (
                        <>
                          {viewingSubtree && (
                            <button
                              className="absolute top-2 right-2 px-2.5 py-1.5 rounded-lg font-semibold text-[11px] sm:text-xs shadow bg-gradient-to-r from-pink-500 to-cyan-500 text-white hover:from-pink-600 hover:to-cyan-600 transition-all z-10 border border-cyan-300"
                              style={{ minWidth: '70px', maxWidth: '120px', lineHeight: 1.2, letterSpacing: '0.01em', boxShadow: '0 1px 4px #38bdf822' }}
                              onClick={async () => {
                                setTreeLoading(true);
                                setViewingSubtree(false);
                                const tree = await fetchFullDownlineTree.call(null, 3, 50);
                                setSelectedDownlineTree(tree);
                                setTreeLoading(false);
                              }}
                            >
                              ← Back
                            </button>
                          )}
                          {selectedDownlineTree && selectedDownlineTree.address ? (
                            <Tree
                              data={mapDownlineTreeToD3(selectedDownlineTree)}
                              orientation="vertical"
                              translate={{ x: 400, y: 120 }}
                              zoomable={true}
                              collapsible={true}
                              transitionDuration={600}
                              separation={{ siblings: 3.5, nonSiblings: 4.5 }}
                              renderCustomNodeElement={({ nodeDatum, toggleNode }) => (
                                <g style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setModalUser(nodeDatum); }}>
                                  <defs>
                                    <radialGradient id={`grad-${nodeDatum.address}`} cx="50%" cy="50%" r="50%">
                                      <stop offset="0%" stopColor="#fff" stopOpacity="0.7" />
                                      <stop offset="100%" stopColor={nodeDatum.isSpecial ? '#f472b6' : nodeDatum.isTopReferrer ? '#38bdf8' : '#22d3ee'} stopOpacity="0.9" />
                                    </radialGradient>
                                  </defs>
                                  <circle r={nodeDatum.__rd3t.depth === 0 ? 30 : 22} fill={`url(#grad-${nodeDatum.address})`} stroke="#fff" strokeWidth="2.5" />
                                  <foreignObject x="-40" y="-60" width="80" height="60">
                                    <div style={{display:'flex',justifyContent:'center',alignItems:'center'}}>
                                      <ENSAvatar address={nodeDatum.address} size={54} />
                                    </div>
                                  </foreignObject>
                                  <Tooltip content={<div style={{minWidth:200}}>
                                    <div className="font-mono text-cyan-300 text-xs mb-1" style={{wordBreak:'break-all'}}>{nodeDatum.address}</div>
                                    {nodeDatum.inviteCode && <div className="text-xs text-pink-400">Invite: {nodeDatum.inviteCode}</div>}
                                    {nodeDatum.earnings && <div className="text-xs text-emerald-400">Earned: {nodeDatum.earnings} USDT</div>}
                                    {nodeDatum.level && <div className="text-xs text-cyan-200">Level: {nodeDatum.level}</div>}
                                    {nodeDatum.joinDate && <div className="text-xs text-yellow-300">Joined: {nodeDatum.joinDate}</div>}
                                  </div>}>
                                    <text fill="#fff" stroke="#000" strokeWidth="0.5" x="0" y="48" textAnchor="middle" fontSize="18" fontWeight="bold">
                                      {nodeDatum.name}
                                    </text>
                                  </Tooltip>
                                  {nodeDatum.isSpecial && (
                                    <Badge label="Special" color="#f472b6" className="absolute left-1/2 -translate-x-1/2 mt-1" />
                                  )}
                                  {nodeDatum.isTopReferrer && (
                                    <Badge label="Top Referrer" color="#38bdf8" className="absolute left-1/2 -translate-x-1/2 mt-1" />
                                  )}
                                </g>
                              )}
                              styles={{
                                nodes: {
                                  node: {
                                    circle: { filter: 'drop-shadow(0 1px 8px #22d3ee66)' },
                                    name: { fontSize: '20px', fill: '#fff', fontWeight: 700, filter: 'drop-shadow(0 1px 2px #fff6)' },
                                    attributes: { fontSize: '16px', fill: '#a7f3d0' },
                                  },
                                  leafNode: {
                                    circle: { filter: 'drop-shadow(0 1px 6px #a7f3d066)' },
                                    name: { fontSize: '18px', fill: '#fff' },
                                    attributes: { fontSize: '14px', fill: '#a1a1aa' },
                                  },
                                },
                                links: { stroke: 'url(#link-gradient)', strokeWidth: 3.5, opacity: 0.95 },
                              }}
                            />
                          ) : (
                            <div className="text-xs text-gray-400 py-2 text-center">No tree data found for this referral. Please try another or refresh.</div>
                          )}
                          <UserModal user={modalUser} onClose={() => setModalUser(null)} />
                        </>
                      ) : (
                        <div className="text-xs text-gray-400 py-2 text-center">Select a referral to view tree</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            // If no children, show message
            return <div className="text-xs text-gray-400 py-2">No referral tree data available</div>;
          })()}
        </div>
      </div>
    </div>
  );
  // Map downlineTree data to react-d3-tree format
  function mapDownlineTreeToD3(node) {
    if (!node) return {};
    // Helper to truncate wallet addresses
    const truncateAddress = addr =>
      typeof addr === 'string' && addr.length > 12
        ? `${addr.slice(0, 6)}...${addr.slice(-4)}`
        : addr;
    // Example: mark special/top referrer (customize as needed)
    const isSpecial = node.isSpecial || false;
    const isTopReferrer = node.directCount && node.directCount >= 10; // e.g., 10+ direct downlines
    return {
      name: truncateAddress(node.address),
      address: node.address,
      level: node.level,
      joinDate: node.joinDate,
      inviteCode: node.inviteCode,
      earnings: node.earnings,
      isSpecial,
      isTopReferrer,
      children: Array.isArray(node.children)
        ? node.children.map(mapDownlineTreeToD3)
        : [],
    };
  }

};

export default UserAnalytics;